/**
 * Balance and payment amounts in NANOCREDITS: 10^-9 of a CREDIT. With
 * `CREDITS_PER_USD = 1`, 1 credit = 1 dollar = 1_000_000_000 nanocredits
 * (`SCALE`), and the minimum unit is $0.000000001.
 *
 * The unit was called "nanocents" until 2026-09-23, and that name was false by
 * exactly 100: a real nanocent is 10^-9 of a CENT, which would put a dollar at
 * 1e11, while the ledger has always been 1e9 per dollar. The name did damage
 * on its own. On 2026-09-22 the same 100x error turned up in four independent
 * places, each written by someone reasoning correctly from the wrong word: the
 * API-keys limit editor wrote rows at 100x, two sets of comments asserted
 * 1 credit = 1e11, and a test asserted 1 USD = 1e12 and then divided by 1e12,
 * so it passed regardless. "1 credit = 1e9 nanocredits" reads as true; do not
 * rename it back. Where "nanocents" still appears it is a frozen boundary, not
 * an oversight: SQL columns (`cost_nanocents`, `price_nanocents`,
 * `budget_nanocents`), Prometheus metric names, the `SPEND_ALERT_DAILY_NANOCENTS`
 * env var, the `pricing.*Nanocents` platform-setting keys, and JSON keys that
 * were already on the wire.
 */

import { CREDITS_PER_USD } from './credits-basis';

const SCALE = BigInt(1e9);
const SCALE_NUM = 1e9;

/**
 * Convert dollars (number) to nanocredits (bigint)
 */
export function toNanocredits(dollars: number): bigint {
  return BigInt(Math.round(dollars * SCALE_NUM));
}

/**
 * @deprecated Compatibility alias for the 2026-09-23 rename. `lib/ws/server.ts`
 * still imports `toNanocents` and was frozen during the rename (WebSocket-auth
 * work in flight in that file), as does the inline TypeScript in
 * `infrastructure/admin.sh`. Nothing new may import this name; the test mocks of
 * this module that answer `toNanocents` beside `toNanocredits` exist for the same
 * reason. Delete all of it in the follow-up that moves `lib/ws/server.ts`.
 */
export const toNanocents = toNanocredits;

/**
 * Convert nanocredits (bigint or number) to dollars (number) for display
 */
export function toDollars(nanocredits: bigint | number): number {
  return Number(nanocredits) / SCALE_NUM;
}

/**
 * Nanocredits to credits, at `CREDITS_PER_USD` (1, see lib/balance/credits-basis.ts).
 *
 * The API answers every credit amount under a `...Credits` name beside the
 * legacy `...Dollars` name (2026-09-20: `totalDollars` on GET /api/balance
 * was a credit balance labelled as dollars, and the owner's instruction was
 * "if totalDollars is on our API we need to rename it to totalCredits"). The
 * two carry the same number today; this is a function rather than an alias
 * of `toDollars` so the credits name keeps meaning credits if the basis ever
 * moves. The `...Dollars` names are owed a removal once consumers migrate.
 */
export function toCredits(nanocredits: bigint | number): number {
  return toDollars(nanocredits) * CREDITS_PER_USD;
}

/**
 * Cents <-> the other two units, integer arithmetic only. Stripe settles in
 * integer cents, the ledger is nanocredits, and the machine-payments rail
 * (lib/payments/mpp*) quotes cents on the wire and dollars-as-decimal in the
 * x402 advertisement, so the three conversions live here beside the dollar
 * helpers rather than as ad hoc `* 1e7` at each call site. No float ever
 * touches the value: 1 cent = 10^7 nanocredits exactly. Both throw on anything
 * but a non-negative integer, because a fractional or negative cent is a
 * caller bug that must not become a quietly rounded price.
 *
 * The name keeps CENT on purpose: cents are Stripe's unit, so the constant names
 * both ends of the conversion, target unit first. Its predecessor
 * `NANOCENTS_PER_CENT = 1e7` was the single most misleading identifier of the
 * old name, since 10^7 nanocents per cent is arithmetically false.
 */
export const NANOCREDITS_PER_CENT = 10_000_000n;

function assertIntegerCents(cents: number, fn: string): void {
  if (!Number.isInteger(cents) || cents < 0) {
    throw new Error(`${fn}: expected a non-negative integer number of cents, got ${cents}`);
  }
}

export function centsToNanocredits(cents: number): bigint {
  assertIntegerCents(cents, 'centsToNanocredits');
  return BigInt(cents) * NANOCREDITS_PER_CENT;
}

/** Integer cents as the "20.00" decimal-dollar string the x402 accepts entries carry. */
export function centsToDecimalDollars(cents: number): string {
  assertIntegerCents(cents, 'centsToDecimalDollars');
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}

/**
 * Format nanocredits as a dollar string (e.g. "$1.50")
 */
export function formatDollars(nanocredits: bigint | number): string {
  const d = toDollars(nanocredits);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 9,
  }).format(d);
}

/**
 * Smart-format a dollar amount with consistent precision.
 * >= $1       -> "1.50"        (2 decimals)
 * $0.01–$1   -> "0.024"       (2–4 decimals, 3 significant digits, trailing zeros trimmed)
 * < $0.01    -> "0.00243"     (4–6 decimals, 3 significant digits)
 * exactly 0  -> "0.00"
 */
export function formatAmount(amount: number): string {
  if (amount === 0) return '0.00';
  const abs = Math.abs(amount);
  if (abs >= 1) return amount.toFixed(2);
  const magnitude = Math.floor(Math.log10(abs));
  const decimals = Math.min(6, Math.max(2, -magnitude + 2));
  const fixed = amount.toFixed(decimals);
  const dot = fixed.indexOf('.');
  if (dot < 0) return fixed;
  let end = fixed.length;
  const minEnd = dot + 3; // 2 decimal places minimum
  while (end > minEnd && fixed[end - 1] === '0') end--;
  return fixed.slice(0, end);
}

/**
 * Format a credit amount with the "credits" unit.
 * e.g. 5.00 -> "5.00 credits", 0.000154 -> "0.000154 credits"
 *
 * THIS IS THE ONLY MONEY FORMATTER FOR USER-FACING COPY, and the reason is
 * regulatory rather than stylistic. Credits are the product's own word and are
 * fine to show; a DOLLAR FIGURE is not, because prices and credit terms are set
 * at purchase time and change prospectively. The failure mode is not one string
 * but two side by side: the notifications panel rendered "You've spent $5.45
 * today" directly above "needs 0.107 credits", from which the rate is derivable
 * off a single screenshot. Two verification passes found that independently on
 * 2026-09-07, and `components/critical-toast.tsx` and
 * `components/notification-item.tsx` each carried their own `$` formatter until
 * then. Nothing outside this module formats an amount for a person.
 */
export function formatCredits(amount: number | string): string {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(n)) return `0 credits`;
  return `${formatAmount(n)} credits`;
}

/**
 * Format nanocredits as a credits string (e.g. "1.50 cr")
 */
export function formatNanocreditsAsCredits(nanocredits: bigint | number): string {
  return formatCredits(toDollars(nanocredits));
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Agent tool charges are stored as `Tool 'generate_image_flux_schnell' fee`,
 * which reaches the wallet as "Usage fee · Tool 'generate_...' fee": a raw
 * identifier plus a doubled "fee". Map the generated tool-name shape
 * (`deriveToolName` in portal-mediagen-skill) back to plain language, and fall
 * back to a de-underscored version of the name for non-media tools.
 */
export function humanizeToolCharge(toolName: string): string {
  const byModality: Array<[RegExp, string]> = [
    [/^generate_image_/, 'Image generation'],
    [/^generate_video_/, 'Video generation'],
    [/^generate_speech_/, 'Speech generation'],
    [/^generate_audio_/, 'Audio generation'],
    [/^generate_music_/, 'Music generation'],
    [/^generate_3d_/, '3D model generation'],
    [/^analyze_/, 'Media analysis'],
  ];
  for (const [re, label] of byModality) {
    if (re.test(toolName)) return label;
  }
  const pretty = toolName.replace(/_/g, ' ').trim();
  return pretty ? `${pretty.charAt(0).toUpperCase()}${pretty.slice(1)}` : 'Tool use';
}

/**
 * Clean up transaction descriptions for display: strip internal identifiers
 * (Stripe session ids, invoice refs, token/charge UUIDs), rewrite dev-facing
 * phrasing, and replace standalone raw nanocredit values (>= 1_000_000) with
 * formatted credit values. Returns null when nothing displayable remains.
 */
export function cleanDescription(desc: string | null): string | null {
  if (!desc) return desc;
  const out = desc
    .replace(/\s*\[cs_[^\]]*\]/g, '')
    .replace(/\s*\(inv:[^)]*\)/g, '')
    // Voice settlements: "voice gemini seconds=184 (no usage telemetry)" /
    // "voice gemini tokens audioIn=... audioOut=..." -> plain language.
    .replace(/^voice\s+\S+\s+seconds=(\d+).*$/i, (_m, s) => `Voice session, ${s}s`)
    .replace(/^voice\s+\S+\s+tokens\s.*$/i, 'Voice session')
    // "LLM proxy: google/gemini-3.5-flash" -> the model name is the info.
    .replace(/^LLM proxy:\s*/i, '')
    // "Tool 'generate_image_flux_schnell' fee" -> "Image generation".
    .replace(/^Tool\s+'([^']+)'\s+fee$/i, (_m, name: string) => humanizeToolCharge(name))
    .replace(/^Per-request agent fee$/i, 'Agent request')
    .replace(/^Token usage markup$/i, 'Agent markup')
    .replace(/\s*\(no usage telemetry\)/gi, '')
    // Balance-source jargon in escrow rows.
    .replace(/\bfrom real balance\b/i, 'from credits')
    .replace(/\bfrom plan balance\b/i, 'from plan credits')
    .replace(/\bfrom earnings balance\b/i, 'from rewards')
    // "Plan credits for token <uuid>" / "Locked for token <uuid>" -> drop the id.
    .replace(/\s*\bfor token\s+[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '')
    .replace(UUID_RE, '')
    .replace(/\b(\d{7,})\b/g, (_match, digits) => {
      const nc = Number(digits);
      if (!Number.isFinite(nc) || nc < 1_000_000) return digits;
      return formatCredits(nc / SCALE_NUM);
    })
    .replace(/\s{2,}/g, ' ')
    // Danglers left by id removal ("Locked for", "Demo token").
    .replace(/[\s:,·-]+$/g, '')
    .trim();
  return out.length > 0 ? out : null;
}

export { SCALE, SCALE_NUM };
